// FLAG:docuAiEnhancements — sizing arithmetic for the receipt chat's large view.
//
// Why a separate module (as with `chat-retry.ts`): `rag-chat-section.tsx` pulls in MUI, Redux, and
// ampersand-app, while the only genuinely error-prone part of resizing is pure arithmetic. It
// belongs where it can be tested without rendering.
//
// The key is the factor of 2: MUI's dialog container centers the paper on BOTH axes. If the width
// grows by X, the right edge moves only X/2 to the right — the handle would lag behind the pointer
// at half speed. The window therefore grows by twice the pointer distance, keeping the corner
// directly under the mouse.

export interface ChatDialogSize {
  width: number
  height: number
}

/** Below this, the history becomes unreadable — bubbles wrap into individual words. */
export const MIN_CHAT_DIALOG_WIDTH = 480
export const MIN_CHAT_DIALOG_HEIGHT = 320

/** MUI's dialog leaves a 32px margin on every side; resizing preserves that gutter. */
export const CHAT_DIALOG_VIEWPORT_MARGIN = 64

/**
 * Leave more horizontal room: the general AI chat button („Max") floats in the bottom-right
 * WINDOW CORNER — fixed position, z-index 1400/1600, and therefore ABOVE the dialog (1300). A
 * handle beneath that button no longer receives
 * clicks, and because the size is remembered, the
 * window would remain enlarged forever: the user could no longer reach the corner to shrink it.
 *
 * The WIDTH therefore keeps the right edge to the left of the button (56px, flush in the corner).
 * One axis is enough — if the corner is left of the button, height does not matter, and height is
 * the scarcer resource in the chat. Cost: a few dozen pixels of maximum width that nobody misses.
 */
export const CHAT_DIALOG_CORNER_RESERVE = 72

/** The minimum beats the maximum: in a tiny window, keeping the chat readable takes priority. */
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max))

/** The dialog is centered: its own margin consumes window space on both sides. */
export const maxChatDialogWidth = (viewportWidth: number) => viewportWidth - 2 * CHAT_DIALOG_CORNER_RESERVE

const fit = (size: ChatDialogSize, viewport: ChatDialogSize): ChatDialogSize => ({
  width: clamp(size.width, MIN_CHAT_DIALOG_WIDTH, maxChatDialogWidth(viewport.width)),
  height: clamp(size.height, MIN_CHAT_DIALOG_HEIGHT, viewport.height - CHAT_DIALOG_VIEWPORT_MARGIN),
})

/**
 * New window size from the pointer distance traveled since grabbing the handle.
 *
 * `start` is the size AT pointer-down (not the current one): this prevents rounding errors from
 * accumulating over a long drag, and dragging back returns exactly to the initial dimensions.
 */
export const resizeChatDialog = (
  start: ChatDialogSize,
  pointerDelta: { dx: number, dy: number },
  viewport: ChatDialogSize,
): ChatDialogSize => fit(
  { width: start.width + 2 * pointerDelta.dx, height: start.height + 2 * pointerDelta.dy },
  viewport,
)

/**
 * Clamp a remembered size to the CURRENT window — the browser may have become smaller between
 * two openings of the chat.
 */
export const fitChatDialogToViewport = (size: ChatDialogSize, viewport: ChatDialogSize): ChatDialogSize =>
  fit(size, viewport)

export default resizeChatDialog
